/**
 * Недельная сводка новых тем (#845, PR 5b; спека
 * docs/plans/2026-10-02-conversation-themes.md §4) — чистая часть: окно,
 * пороги, сроки, сведение фраз `suggested_theme` и счёт разговоров кандидата.
 * Одна реализация для workflows-api (сводка, пересчёт, сверка) и edge-api
 * (принятие): пороги и окна не должны расходиться.
 *
 * **Фраза** — ключ `normalizeThemeKey` (та же нормализация, что у `norm_key`
 * тем, `topicPairKey`): SQL отдаёт СЫРЫЕ строки `suggested_theme` с числом
 * разговоров, сведение живёт только в JS. У разговора один текущий анализ и в
 * нём одна `suggested_theme`, поэтому группы сырых фраз по разговорам не
 * пересекаются: сумма их счётов по ключам кандидата — это `COUNT(DISTINCT
 * record)` разговоров кандидата. Счёт кандидата — по его СОХРАНЁННЫМ ключам
 * (`phrase_keys`), не по названию и не по ответу модели.
 *
 * Без импорта значений из `@aica/api-types` (кольцо core ↔ api-types, #1076):
 * константы — здесь, контракт Zod берёт их отсюда.
 */
import { topicPairKey } from './theme-bootstrap';

/** Окно сводки и пересчёта — последние 30 дней (§4). */
export const THEME_PROPOSALS_WINDOW_DAYS = 30;

/** «Новые разговоры за неделю» — условие недельного прогона организации (§4). */
export const THEME_PROPOSALS_ACTIVITY_DAYS = 7;

/** Потолок фраз одного прогона (§4, требование ревью #845). */
export const THEME_PROPOSALS_MAX_PHRASES = 300;

/** Сколько кандидатов модель может вернуть за прогон (§4). */
export const THEME_PROPOSALS_MAX_CANDIDATES = 5;

/** Порог Р3: не меньше 10 разговоров… */
export const THEME_PROPOSAL_MIN_CALLS = 10;

/** …и не меньше 3 % разговоров периода (Р3). */
export const THEME_PROPOSAL_MIN_SHARE = 0.03;

/** Тишина ключа после «Отклонить» (§4). */
export const THEME_PROPOSAL_SILENCE_DAYS = 90;

/** Примеров-разговоров у предложения (экран: 2–3). */
export const THEME_PROPOSAL_EXAMPLES = 3;

/**
 * Потолок ключа кандидата ПОСЛЕ нормализации. Название ≤ 60 знаков, но NFKC
 * умеет удлинять («ﬁ» → «fi», «㎏» → «kg»): кандидат, чей ключ вышел длиннее,
 * отбрасывается, а не обрезается — обрезанный ключ совпал бы с чужим.
 */
export const THEME_PROPOSAL_NORM_KEY_MAX = 60;

/** `accepting` старше этого разбирает сверка (§4). */
export const THEME_PROPOSAL_ACCEPTING_STALE_MS = 10 * 60 * 1000;

/** `reserved` в D1 без `accepting` в Postgres старше этого удаляется сверкой (§4). */
export const THEME_PROPOSAL_RESERVED_STALE_MS = 24 * 60 * 60 * 1000;

/**
 * Страховка недельной чистки: `accepting`, который сверка так и не разобрала
 * (сбой D1 или Postgres неделями), удаляется — ни одна строка не живёт вечно.
 */
export const THEME_PROPOSAL_ACCEPTING_ABANDON_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Окно `[start, end)` сводки на момент `end`. */
export function themeProposalsWindow(end: Date): { start: Date; end: Date } {
  return { start: new Date(end.getTime() - THEME_PROPOSALS_WINDOW_DAYS * DAY_MS), end };
}

/** Окно «новых разговоров за неделю» на момент `end`. */
export function themeProposalsActivityWindow(end: Date): { start: Date; end: Date } {
  return { start: new Date(end.getTime() - THEME_PROPOSALS_ACTIVITY_DAYS * DAY_MS), end };
}

/** Пример-разговор фразы: id записи и её время (мс эпохи), свежие первыми. */
export interface ProposalExample {
  recordId: string;
  createdAtMs: number;
}

/** Строка источника, как её отдаёт SQL: сырая фраза, разговоров, свежие примеры. */
export interface ProposalPhraseGroup {
  phrase: string;
  calls: number;
  examples: ProposalExample[];
}

/** Фраза для модели: ключ и разговоров у него. Номер — позиция + 1. */
export interface ProposalPhrase {
  key: string;
  calls: number;
}

/**
 * Ключи набора, которые значат «это уже тема»: `active` и `archived`. `reserved` —
 * резерв принятия в полёте (шаг 1), а не тема: предложение с таким ключом не
 * прячется и не удаляется — повторное «Принять» берёт резерв. Один помощник на
 * экран (`GET .../proposals`) и недельный прогон: их правила не должны расходиться.
 */
export function settledThemeKeys(
  themes: ReadonlyArray<{ normKey: string; status: string }>
): Set<string> {
  return new Set(
    themes.filter((theme) => theme.status !== 'reserved').map((theme) => theme.normKey)
  );
}

/** Ключ фразы — `normalizeThemeKey` с обрезкой, как у фраз стартового предложения. */
export function proposalPhraseKey(phrase: string): string {
  return topicPairKey(phrase);
}

/**
 * Один проход по группам источника: ключ и редакция (`sendable`) каждой группы
 * считаются ОДИН раз. Группа, которую не пропускает редакция (сырая фраза И её
 * ключ), в счёт не входит — её разговоры идут в `withheld`.
 */
function scanProposalGroups(
  groups: readonly ProposalPhraseGroup[],
  sendable: (phrase: string) => boolean
): {
  byKey: Map<string, { calls: number; examples: ProposalExample[] }>;
  withheld: number;
} {
  const byKey = new Map<string, { calls: number; examples: ProposalExample[] }>();
  let withheld = 0;
  for (const group of groups) {
    if (!(group.calls > 0)) continue;
    const key = proposalPhraseKey(group.phrase);
    if (!key) continue;
    if (!sendable(group.phrase)) {
      withheld += group.calls;
      continue;
    }
    const entry = byKey.get(key) ?? { calls: 0, examples: [] };
    entry.calls += group.calls;
    entry.examples.push(...group.examples);
    byKey.set(key, entry);
  }
  return { byKey, withheld };
}

function phrasesOf(
  byKey: ReadonlyMap<string, { calls: number }>,
  max: number
): { phrases: ProposalPhrase[]; capped: boolean } {
  const all = [...byKey.entries()]
    .map(([key, entry]) => ({ key, calls: entry.calls }))
    .sort((a, b) => b.calls - a.calls || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return { phrases: all.slice(0, max), capped: all.length > max };
}

/**
 * Счёт по ключам источника окна: разговоров и свежих примеров у каждого ключа,
 * только по группам, которые пропускает редакция (то же правило, по которому
 * фразы уходили в модель). Читается один раз на прогон и служит и новым
 * кандидатам, и пересчёту сохранённых предложений.
 */
export interface ProposalKeyIndex {
  byKey: ReadonlyMap<string, { calls: number; examples: ProposalExample[] }>;
}

/**
 * Группы источника → фразы для модели И счёт по ключам, одним проходом (ключ и
 * редакция каждой группы — один раз): ключ, сведение одинаковых ключей, самые
 * частые первыми (при равенстве — по ключу), не больше `max`. Не пропущенная
 * редакцией группа в модель не идёт и в счёт не входит — её разговоров ровно
 * `withheld`.
 */
export function analyzeProposalGroups(
  groups: readonly ProposalPhraseGroup[],
  sendable: (phrase: string) => boolean,
  max: number = THEME_PROPOSALS_MAX_PHRASES
): {
  phrases: ProposalPhrase[];
  withheld: number;
  capped: boolean;
  index: ProposalKeyIndex;
} {
  const { byKey, withheld } = scanProposalGroups(groups, sendable);
  return { ...phrasesOf(byKey, max), withheld, index: { byKey } };
}

export interface ProposalCount {
  /** Разговоров кандидата в окне — сумма по его ключам. */
  calls: number;
  /** Ключи кандидата, которые в окне есть (обрезка набора по окну). */
  liveKeys: string[];
  /** До `THEME_PROPOSAL_EXAMPLES` свежих разговоров, без повторов. */
  exampleIds: string[];
}

/** Счёт кандидата по его ключам — SQL дал группы, ключи дал сохранённый набор. */
export function countProposalKeys(
  index: ProposalKeyIndex,
  keys: readonly string[]
): ProposalCount {
  let calls = 0;
  const liveKeys: string[] = [];
  const examples: ProposalExample[] = [];
  for (const key of new Set(keys)) {
    const entry = index.byKey.get(key);
    if (!entry) continue;
    calls += entry.calls;
    liveKeys.push(key);
    examples.push(...entry.examples);
  }
  examples.sort(
    (a, b) =>
      b.createdAtMs - a.createdAtMs ||
      (a.recordId < b.recordId ? -1 : a.recordId > b.recordId ? 1 : 0)
  );
  const exampleIds: string[] = [];
  for (const example of examples) {
    if (exampleIds.length >= THEME_PROPOSAL_EXAMPLES) break;
    if (!exampleIds.includes(example.recordId)) exampleIds.push(example.recordId);
  }
  return { calls, liveKeys: liveKeys.sort(), exampleIds };
}

/**
 * Порог Р3 — по сырым счётчикам, до округления доли: ≥ 10 разговоров и
 * ≥ 3 % разговоров периода. Пустой период — не порог.
 */
export function passesProposalThreshold(calls: number, totalCalls: number): boolean {
  return (
    totalCalls > 0 &&
    calls >= THEME_PROPOSAL_MIN_CALLS &&
    calls >= THEME_PROPOSAL_MIN_SHARE * totalCalls
  );
}

/** Доля для хранения — NUMERIC(5,4): 0..1, четыре знака. */
export function proposalShare(calls: number, totalCalls: number): number {
  if (!(totalCalls > 0)) return 0;
  return Math.min(1, Math.round((calls / totalCalls) * 10_000) / 10_000);
}
